/* eslint-disable @typescript-eslint/no-explicit-any -- form default values type */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { subscriptionPlanApi } from '@/services/subscriptionApi';
import { paymentGatewayApi } from '@/services/paymentGatewayApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import type { SubscriptionPlan, SubscriptionPlanRequest, PlanStatus, PlanType, BillingCycle, PaymentGateway } from '@/types/subscription';
import type { PaymentMethodsResponse } from '@/types/paymentGateway';

// Zod schema with conditional validation
const planSchema = z
  .object({
    planCode: z.string().regex(/^[A-Z0-9_-]+$/, 'Plan code must be uppercase alphanumeric with underscores/hyphens').min(3).max(50),
    displayName: z.string().min(1, 'Display name is required').max(100),
    description: z.string().min(1, 'Description is required').max(500),
    monthlyPrice: z.number().min(0, 'Monthly price must be >= 0').multipleOf(0.01),
    yearlyPrice: z.number().min(0, 'Yearly price must be >= 0').multipleOf(0.01),
    billingCycle: z.enum(['MONTHLY', 'YEARLY']),
    planType: z.enum(['FREE', 'BASIC', 'PREMIUM', 'ENTERPRISE', 'PRO', 'CUSTOM']),
    currency: z.string().regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter ISO code'),
    supportedCountries: z.array(z.string().regex(/^[A-Z]{2}$/)).optional(),
    supportedCurrencies: z.array(z.string().regex(/^[A-Z]{3}$/)).optional(),
    supportedPaymentGateways: z.array(z.enum(['STRIPE', 'PAYPAL', 'RAZORPAY', 'SQUARE', 'ADYEN', 'BRAINTREE', 'CHECKOUT_COM', 'WORLDPAY', 'PAYU', 'MOLLIE', 'KLARNA', 'APPLE_PAY', 'GOOGLE_PAY', 'AMAZON_PAY', 'ALIPAY', 'WECHAT_PAY'])).optional(),
    invoiceDueDays: z.number().min(1).max(90).optional(),
    status: z.enum(['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED']),
    features: z.record(z.string(), z.unknown()).optional(),
    limits: z.record(z.string(), z.number()).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    displayOrder: z.number().optional(),
    parentPlanId: z.string().optional(),
    trialEnabled: z.boolean().optional(),
    trialDurationDays: z.number().min(1).max(365).optional(),
    isPopular: z.boolean().optional(),
    isVisible: z.boolean().optional(),
    availableFrom: z.string().optional(),
    availableUntil: z.string().optional(),
    affiliatorGroupIds: z.array(z.string()).optional(),
    // Display & Marketing
    tagline: z.string().max(200).optional(),
    ctaText: z.string().max(100).optional(),
    badgeText: z.string().max(50).optional(),
    iconName: z.string().optional(),
    recommendedFor: z.string().max(200).optional(),
    savingsPercentage: z.number().min(0).max(100).optional(),
    // Limits & Quotas
    dailyTokenLimit: z.number().optional(),
    monthlyTokenLimit: z.number().optional(),
    maxCoachSessionsPerDay: z.number().optional(),
    maxLearningPaths: z.number().optional(),
    // Feature Highlights & Flags
    featureHighlights: z.array(z.string()).optional(),
    featureFlags: z.record(z.string(), z.boolean()).optional(),
  })
  .refine(
    (data) => {
      if (data.trialEnabled) {
        return data.trialDurationDays !== undefined && data.trialDurationDays >= 1;
      }
      return true;
    },
    {
      message: 'Trial duration is required when trial is enabled',
      path: ['trialDurationDays'],
    }
  ) as any;

type PlanFormData = z.infer<typeof planSchema>;

const PLAN_TYPES: PlanType[] = ['FREE', 'BASIC', 'PREMIUM', 'ENTERPRISE', 'PRO', 'CUSTOM'];
const BILLING_CYCLES: BillingCycle[] = ['MONTHLY', 'YEARLY'];
const STATUSES: PlanStatus[] = ['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED'];
const COMMON_COUNTRIES = ['US', 'GB', 'CA', 'AU', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'CH', 'AT', 'SE', 'NO', 'DK', 'FI', 'IE', 'PT', 'GR', 'PL'];

export const SubscriptionPlanFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [autoGenerateCode, setAutoGenerateCode] = useState(false);

  const form = useForm<PlanFormData>({
    resolver: zodResolver(planSchema),
    defaultValues: {
      planCode: '',
      displayName: '',
      description: '',
      monthlyPrice: 0,
      yearlyPrice: 0,
      billingCycle: 'MONTHLY',
      planType: 'BASIC',
      currency: 'USD',
      supportedCountries: [],
      supportedCurrencies: [],
      supportedPaymentGateways: [],
      invoiceDueDays: 7,
      status: 'DRAFT',
      features: {},
      limits: {},
      metadata: {},
      trialEnabled: true,
      isPopular: false,
      isVisible: true,
      affiliatorGroupIds: [],
      tagline: '',
      ctaText: '',
      badgeText: '',
      iconName: '',
      recommendedFor: '',
      savingsPercentage: undefined,
      dailyTokenLimit: undefined,
      monthlyTokenLimit: undefined,
      maxCoachSessionsPerDay: undefined,
      maxLearningPaths: undefined,
      featureHighlights: [],
      featureFlags: {},
    },
  });

  const watchedDisplayName = form.watch('displayName');
  const watchedTrialEnabled = form.watch('trialEnabled');
  const [featureEntries, setFeatureEntries] = useState<Array<{ key: string; value: string }>>([]);
  const [limitEntries, setLimitEntries] = useState<Array<{ key: string; value: string }>>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [selectedGateways, setSelectedGateways] = useState<PaymentGateway[]>([]);
  const [highlightEntries, setHighlightEntries] = useState<string[]>([]);
  const [flagEntries, setFlagEntries] = useState<Array<{ key: string; value: boolean }>>([]);

  // Fetch payment methods from API
  const {
    data: paymentMethodsResponse,
    isLoading: isLoadingPaymentMethods,
  } = useQuery<PaymentMethodsResponse>({
    queryKey: ['paymentMethods'],
    queryFn: async () => {
      return await paymentGatewayApi.getPaymentMethods();
    },
  });

  // Auto-generate plan code from display name
  useEffect(() => {
    if (autoGenerateCode && watchedDisplayName && !isEdit) {
      const generated = watchedDisplayName
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      form.setValue('planCode', generated);
    }
  }, [watchedDisplayName, autoGenerateCode, isEdit, form]);

  // Fetch subscription plan data for edit mode
  const {
    data: _subscriptionPlan,
    isLoading: isLoadingPlan,
    error: planError,
  } = useEntity<SubscriptionPlan>({
    fetchFn: (planId: string) => subscriptionPlanApi.getById(planId),
    entityId: id,
    entityName: 'subscriptionPlan',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        planCode: data.planCode,
        displayName: data.displayName,
        description: data.description,
        monthlyPrice: data.monthlyPrice,
        yearlyPrice: data.yearlyPrice,
        billingCycle: data.billingCycle,
        planType: data.planType,
        currency: data.currency,
        supportedCountries: data.supportedCountries || [],
        supportedCurrencies: data.supportedCurrencies || [],
        supportedPaymentGateways: data.supportedPaymentGateways || [],
        invoiceDueDays: data.invoiceDueDays || 7,
        status: data.status,
        features: data.features || {},
        limits: data.limits || {},
        metadata: data.metadata || {},
        displayOrder: data.displayOrder,
        parentPlanId: data.parentPlanId,
        trialEnabled: data.trialEnabled ?? true,
        trialDurationDays: data.trialDurationDays,
        isPopular: data.isPopular ?? false,
        isVisible: data.isVisible ?? true,
        availableFrom: data.availableFrom,
        availableUntil: data.availableUntil,
        affiliatorGroupIds: data.affiliatorGroupIds || [],
        tagline: data.tagline || '',
        ctaText: data.ctaText || '',
        badgeText: data.badgeText || '',
        iconName: data.iconName || '',
        recommendedFor: data.recommendedFor || '',
        savingsPercentage: data.savingsPercentage,
        dailyTokenLimit: data.dailyTokenLimit,
        monthlyTokenLimit: data.monthlyTokenLimit,
        maxCoachSessionsPerDay: data.maxCoachSessionsPerDay,
        maxLearningPaths: data.maxLearningPaths,
        featureHighlights: data.featureHighlights || [],
        featureFlags: data.featureFlags || {},
      });
      setSelectedCountries(data.supportedCountries || []);
      setSelectedCurrencies(data.supportedCurrencies || []);
      setSelectedGateways(data.supportedPaymentGateways || []);
      setFeatureEntries(
        Object.entries(data.features || {}).map(([key, value]) => ({ key, value: String(value) }))
      );
      setLimitEntries(
        Object.entries(data.limits || {}).map(([key, value]) => ({ key, value: String(value) }))
      );
      setHighlightEntries(data.featureHighlights || []);
      setFlagEntries(
        Object.entries(data.featureFlags || {}).map(([key, value]) => ({ key, value: Boolean(value) }))
      );
    },
    onError: () => {
      navigate('/admin/subscription-plans');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: PlanFormData) => {
      const payload: SubscriptionPlanRequest = {
        ...data,
        supportedCountries: selectedCountries,
        supportedCurrencies: selectedCurrencies,
        supportedPaymentGateways: selectedGateways,
        features: featureEntries.reduce((acc, entry) => {
          if (entry.key) {
            acc[entry.key] = entry.value;
          }
          return acc;
        }, {} as Record<string, unknown>),
        limits: limitEntries.reduce((acc, entry) => {
          if (entry.key) {
            const numValue = parseInt(entry.value, 10);
            if (!isNaN(numValue)) {
              acc[entry.key] = numValue;
            }
          }
          return acc;
        }, {} as Record<string, number>),
        featureHighlights: highlightEntries.filter((h) => h.trim() !== ''),
        featureFlags: flagEntries.reduce((acc, entry) => {
          if (entry.key) {
            acc[entry.key] = entry.value;
          }
          return acc;
        }, {} as Record<string, boolean>),
      };
      return await subscriptionPlanApi.create(payload);
    },
    entityName: 'subscriptionPlan',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/subscription-plans');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: PlanFormData) => {
      if (!id) throw new Error('Subscription Plan ID is required');
      const payload: SubscriptionPlanRequest = {
        ...data,
        supportedCountries: selectedCountries,
        supportedCurrencies: selectedCurrencies,
        supportedPaymentGateways: selectedGateways,
        features: featureEntries.reduce((acc, entry) => {
          if (entry.key) {
            acc[entry.key] = entry.value;
          }
          return acc;
        }, {} as Record<string, unknown>),
        limits: limitEntries.reduce((acc, entry) => {
          if (entry.key) {
            const numValue = parseInt(entry.value, 10);
            if (!isNaN(numValue)) {
              acc[entry.key] = numValue;
            }
          }
          return acc;
        }, {} as Record<string, number>),
        featureHighlights: highlightEntries.filter((h) => h.trim() !== ''),
        featureFlags: flagEntries.reduce((acc, entry) => {
          if (entry.key) {
            acc[entry.key] = entry.value;
          }
          return acc;
        }, {} as Record<string, boolean>),
      };
      return await subscriptionPlanApi.update(id, payload);
    },
    entityName: 'subscriptionPlan',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/subscription-plans');
    },
  });

  const onSubmit = async (data: PlanFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  // Derive available gateways and currencies from payment methods API
  const availableGateways = useMemo(() => {
    if (!paymentMethodsResponse?.paymentMethods) return [];
    const gatewaySet = new Set<PaymentGateway>();
    paymentMethodsResponse.paymentMethods.forEach((methodInfo) => {
      if (methodInfo.supportedByGateways) {
        methodInfo.supportedByGateways.forEach((gateway) => {
          // Filter out 'MOCK' as it's not in subscription PaymentGateway type
          if (gateway.gatewayType !== 'MOCK') {
            // Type assertion needed because paymentGateway.PaymentGateway includes 'MOCK'
            // but subscription.PaymentGateway doesn't
            gatewaySet.add(gateway.gatewayType as PaymentGateway);
          }
        });
      }
    });
    return Array.from(gatewaySet).sort();
  }, [paymentMethodsResponse]);

  const availableCurrencies = useMemo(() => {
    if (!paymentMethodsResponse?.paymentMethods) return [];
    return paymentMethodsResponse.supportedCurrencies || [];
  }, [paymentMethodsResponse]);

  const isLoading = isLoadingPlan;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading subscription plan..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && planError) {
    return <ErrorState error={planError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/subscription-plans">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Plan' : 'Create Plan'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update plan information' : 'Create a new subscription plan'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Plan identification and classification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={autoGenerateCode}
                  onChange={(e) => setAutoGenerateCode(e.target.checked)}
                  disabled={isEdit}
                  className="rounded border-gray-300"
                />
                <Label className="text-sm">Auto-generate plan code from display name</Label>
              </div>

              <FormField
                control={form.control}
                name="planCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Plan Code *</FormLabel>
                    <FormControl>
                      <Input placeholder="PLAN_CODE" {...field} className="font-mono" />
                    </FormControl>
                    <FormDescription>Uppercase alphanumeric with underscores/hyphens (3-50 chars)</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter plan display name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter plan description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <FormField
                  control={form.control}
                  name="planType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Plan Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select plan type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PLAN_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="billingCycle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Billing Cycle *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select billing cycle" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {BILLING_CYCLES.map((cycle) => (
                            <SelectItem key={cycle} value={cycle}>
                              {cycle}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency *</FormLabel>
                      <FormControl>
                        <Input placeholder="USD" {...field} className="font-mono" maxLength={3} />
                      </FormControl>
                      <FormDescription>ISO 3-letter code</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle>Pricing</CardTitle>
              <CardDescription>Plan pricing information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="monthlyPrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monthly Price *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="yearlyPrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Yearly Price *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="invoiceDueDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Due Days</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max="90"
                        placeholder="7"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 7)}
                      />
                    </FormControl>
                    <FormDescription>Days until invoice is due (1-90)</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Geographical Support */}
          <Card>
            <CardHeader>
              <CardTitle>Geographical Support</CardTitle>
              <CardDescription>Supported countries, currencies, and payment gateways</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Supported Countries (ISO 2-letter codes)</Label>
                <div className="flex flex-wrap gap-2">
                  {COMMON_COUNTRIES.map((country) => (
                    <button
                      key={country}
                      type="button"
                      onClick={() => {
                        const newCountries = selectedCountries.includes(country)
                          ? selectedCountries.filter((c) => c !== country)
                          : [...selectedCountries, country];
                        setSelectedCountries(newCountries);
                        form.setValue('supportedCountries', newCountries);
                      }}
                      className={`px-2 py-1 rounded-md text-sm font-mono ${
                        selectedCountries.includes(country)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary text-secondary-foreground'
                      }`}
                    >
                      {country}
                    </button>
                  ))}
                </div>
                <Input
                  placeholder="Add custom country code (e.g., US)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const value = e.currentTarget.value.toUpperCase().trim();
                      if (value && /^[A-Z]{2}$/.test(value) && !selectedCountries.includes(value)) {
                        const newCountries = [...selectedCountries, value];
                        setSelectedCountries(newCountries);
                        form.setValue('supportedCountries', newCountries);
                        e.currentTarget.value = '';
                      }
                    }
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label>Supported Currencies (ISO 3-letter codes)</Label>
                {isLoadingPaymentMethods ? (
                  <p className="text-sm text-muted-foreground">Loading currencies...</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableCurrencies.map((currency) => (
                      <button
                        key={currency}
                        type="button"
                        onClick={() => {
                          const newCurrencies = selectedCurrencies.includes(currency)
                            ? selectedCurrencies.filter((c) => c !== currency)
                            : [...selectedCurrencies, currency];
                          setSelectedCurrencies(newCurrencies);
                          form.setValue('supportedCurrencies', newCurrencies);
                        }}
                        className={`px-2 py-1 rounded-md text-sm font-mono ${
                          selectedCurrencies.includes(currency)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary text-secondary-foreground'
                        }`}
                      >
                        {currency}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Supported Payment Gateways</Label>
                {isLoadingPaymentMethods ? (
                  <p className="text-sm text-muted-foreground">Loading payment gateways...</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {availableGateways.map((gateway) => (
                      <button
                        key={gateway}
                        type="button"
                        onClick={() => {
                          const newGateways = selectedGateways.includes(gateway)
                            ? selectedGateways.filter((g) => g !== gateway)
                            : [...selectedGateways, gateway];
                          setSelectedGateways(newGateways);
                          form.setValue('supportedPaymentGateways', newGateways);
                        }}
                        className={`px-2 py-1 rounded-md text-sm text-left ${
                          selectedGateways.includes(gateway)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary text-secondary-foreground'
                        }`}
                      >
                        {gateway.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Features & Limits */}
          <Card>
            <CardHeader>
              <CardTitle>Features & Limits</CardTitle>
              <CardDescription>Plan features and limits configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <Label>Features</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFeatureEntries([...featureEntries, { key: '', value: '' }])}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Feature
                  </Button>
                </div>
                <div className="space-y-2">
                  {featureEntries.map((entry, index) => (
                    <div key={index} className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                      <Input
                        placeholder="Feature key"
                        value={entry.key}
                        onChange={(e) => {
                          const newEntries = [...featureEntries];
                          newEntries[index].key = e.target.value;
                          setFeatureEntries(newEntries);
                        }}
                      />
                      <Input
                        placeholder="Feature value"
                        value={entry.value}
                        onChange={(e) => {
                          const newEntries = [...featureEntries];
                          newEntries[index].value = e.target.value;
                          setFeatureEntries(newEntries);
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setFeatureEntries(featureEntries.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <Label>Limits (integer values only)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setLimitEntries([...limitEntries, { key: '', value: '' }])}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Limit
                  </Button>
                </div>
                <div className="space-y-2">
                  {limitEntries.map((entry, index) => (
                    <div key={index} className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                      <Input
                        placeholder="Limit key"
                        value={entry.key}
                        onChange={(e) => {
                          const newEntries = [...limitEntries];
                          newEntries[index].key = e.target.value;
                          setLimitEntries(newEntries);
                        }}
                      />
                      <Input
                        type="number"
                        placeholder="Limit value"
                        value={entry.value}
                        onChange={(e) => {
                          const newEntries = [...limitEntries];
                          newEntries[index].value = e.target.value;
                          setLimitEntries(newEntries);
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setLimitEntries(limitEntries.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trial Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Trial Settings</CardTitle>
              <CardDescription>Trial configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="trialEnabled"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value ?? true}
                        onChange={field.onChange}
                        className="rounded border-gray-300"
                      />
                    </FormControl>
                    <FormLabel>Trial Enabled</FormLabel>
                  </FormItem>
                )}
              />

              {watchedTrialEnabled && (
                <FormField
                  control={form.control}
                  name="trialDurationDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Trial Duration (Days) *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          placeholder="14"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10) || undefined)}
                        />
                      </FormControl>
                      <FormDescription>Required when trial is enabled (1-365 days)</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </CardContent>
          </Card>

          {/* Display Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Display Settings</CardTitle>
              <CardDescription>Plan visibility and display options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="displayOrder"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Order</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="0"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10) || undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isPopular"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value ?? false}
                          onChange={field.onChange}
                          className="rounded border-gray-300"
                        />
                      </FormControl>
                      <FormLabel>Mark as Popular</FormLabel>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="isVisible"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value ?? true}
                        onChange={field.onChange}
                        className="rounded border-gray-300"
                      />
                    </FormControl>
                    <FormLabel>Visible to Users</FormLabel>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Display & Marketing */}
          <Card>
            <CardHeader>
              <CardTitle>Display & Marketing</CardTitle>
              <CardDescription>Marketing copy and display configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="tagline"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tagline</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Start your career growth journey" {...field} />
                    </FormControl>
                    <FormDescription>Short marketing tagline for the plan</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="ctaText"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CTA Text</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Get Started Free" {...field} />
                      </FormControl>
                      <FormDescription>Call-to-action button text</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="badgeText"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Badge Text</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Most Popular" {...field} />
                      </FormControl>
                      <FormDescription>Optional badge label</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="iconName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Icon Name</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ''}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select an icon" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="sparkles">sparkles</SelectItem>
                          <SelectItem value="rocket">rocket</SelectItem>
                          <SelectItem value="building">building</SelectItem>
                          <SelectItem value="crown">crown</SelectItem>
                          <SelectItem value="star">star</SelectItem>
                          <SelectItem value="zap">zap</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="savingsPercentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Savings Percentage</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          placeholder="0"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            field.onChange(val === '' ? undefined : parseInt(val, 10));
                          }}
                        />
                      </FormControl>
                      <FormDescription>Percentage savings vs monthly billing (0-100)</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="recommendedFor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recommended For</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Professionals serious about career growth" {...field} />
                    </FormControl>
                    <FormDescription>Target audience description</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Limits & Quotas */}
          <Card>
            <CardHeader>
              <CardTitle>Limits & Quotas</CardTitle>
              <CardDescription>Token and session limits (-1 = unlimited)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="dailyTokenLimit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Daily Token Limit</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="-1 for unlimited"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            field.onChange(val === '' ? undefined : parseInt(val, 10));
                          }}
                        />
                      </FormControl>
                      <FormDescription>-1 = unlimited</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="monthlyTokenLimit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monthly Token Limit</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="-1 for unlimited"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            field.onChange(val === '' ? undefined : parseInt(val, 10));
                          }}
                        />
                      </FormControl>
                      <FormDescription>-1 = unlimited</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="maxCoachSessionsPerDay"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Coach Sessions Per Day</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="-1 for unlimited"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            field.onChange(val === '' ? undefined : parseInt(val, 10));
                          }}
                        />
                      </FormControl>
                      <FormDescription>-1 = unlimited</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="maxLearningPaths"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Learning Paths</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="-1 for unlimited"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            field.onChange(val === '' ? undefined : parseInt(val, 10));
                          }}
                        />
                      </FormControl>
                      <FormDescription>-1 = unlimited</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Feature Highlights */}
          <Card>
            <CardHeader>
              <CardTitle>Feature Highlights</CardTitle>
              <CardDescription>Marketing bullet points displayed on the pricing page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <Label>Highlights</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newHighlights = [...highlightEntries, ''];
                    setHighlightEntries(newHighlights);
                    form.setValue('featureHighlights', newHighlights);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Highlight
                </Button>
              </div>
              <div className="space-y-2">
                {highlightEntries.map((entry, index) => (
                  <div key={index} className="grid gap-2 md:grid-cols-[1fr_auto]">
                    <Input
                      placeholder="e.g. Unlimited AI Coach conversations"
                      value={entry}
                      onChange={(e) => {
                        const newHighlights = [...highlightEntries];
                        newHighlights[index] = e.target.value;
                        setHighlightEntries(newHighlights);
                        form.setValue('featureHighlights', newHighlights);
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newHighlights = highlightEntries.filter((_, i) => i !== index);
                        setHighlightEntries(newHighlights);
                        form.setValue('featureHighlights', newHighlights);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {highlightEntries.length === 0 && (
                  <p className="text-sm text-muted-foreground">No highlights added yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Feature Flags */}
          <Card>
            <CardHeader>
              <CardTitle>Feature Flags</CardTitle>
              <CardDescription>Toggle feature flags for this plan</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <Label>Flags</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newFlags = [...flagEntries, { key: '', value: false }];
                    setFlagEntries(newFlags);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Flag
                </Button>
              </div>
              <div className="space-y-2">
                {flagEntries.map((entry, index) => (
                  <div key={index} className="grid gap-2 md:grid-cols-[1fr_auto_auto] items-center">
                    <Input
                      placeholder="Flag key (e.g. advancedAnalytics)"
                      value={entry.key}
                      onChange={(e) => {
                        const newFlags = [...flagEntries];
                        newFlags[index] = { ...newFlags[index], key: e.target.value };
                        setFlagEntries(newFlags);
                        form.setValue('featureFlags', newFlags.reduce((acc, f) => {
                          if (f.key) acc[f.key] = f.value;
                          return acc;
                        }, {} as Record<string, boolean>));
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newFlags = [...flagEntries];
                        newFlags[index] = { ...newFlags[index], value: !newFlags[index].value };
                        setFlagEntries(newFlags);
                        form.setValue('featureFlags', newFlags.reduce((acc, f) => {
                          if (f.key) acc[f.key] = f.value;
                          return acc;
                        }, {} as Record<string, boolean>));
                      }}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        entry.value
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                      }`}
                    >
                      {entry.value ? 'ON' : 'OFF'}
                    </button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newFlags = flagEntries.filter((_, i) => i !== index);
                        setFlagEntries(newFlags);
                        form.setValue('featureFlags', newFlags.reduce((acc, f) => {
                          if (f.key) acc[f.key] = f.value;
                          return acc;
                        }, {} as Record<string, boolean>));
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {flagEntries.length === 0 && (
                  <p className="text-sm text-muted-foreground">No feature flags added yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Availability Dates */}
          <Card>
            <CardHeader>
              <CardTitle>Availability</CardTitle>
              <CardDescription>Plan availability dates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="availableFrom"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Available From</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          {...field}
                          value={field.value ? new Date(field.value).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value ? new Date(value).toISOString() : undefined);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="availableUntil"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Available Until</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          {...field}
                          value={field.value ? new Date(field.value).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value ? new Date(value).toISOString() : undefined);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/subscription-plans">Cancel</Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.setValue('status', 'DRAFT');
                form.handleSubmit(onSubmit)();
              }}
              disabled={mutation.isLoading}
            >
              Save as Draft
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default SubscriptionPlanFormPage;
